import dateFormat from 'dateformat';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { faCircleCheck, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText } from '@morressier/alembic';
import { IPublishingModel, IRoleType } from '@publishing-engine/domain/models';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { FormFieldPublishingModel } from '@publishing-engine/domain/types/article';
import { ReadonlyCopyrightForm } from 'src/components/Submission/CopyrightForm/ReadonlyCopyrightForm';
import { openFile } from 'src/utils/file';

interface Props {
  organizationName?: string;
}

type ISubmittedByType = Exclude<IRoleType, 'reviewer'>;

const copyrightSubmissionHeading = (submittedBy: ISubmittedByType) => {
  let heading =
    'Copyright and Publication Agreement Uploaded by Editorial Team';

  if (submittedBy === 'author') {
    heading = 'Copyright and Publication Agreement Concluded';
  }

  return heading;
};

const copyrightSubmissionDescription = (
  organizationName: string,
  publishingModel: Pick<IPublishingModel, 'model'>,
  copyrightSubmittedDate: string,
  submittedBy: ISubmittedByType
) => {
  let description = `You have granted ${organizationName} a CC BY 4.0 licence to publish
            your work in Open Access on ${dateFormat(
              copyrightSubmittedDate,
              'mmmm d, yyyy'
            )}. This licence
            allows others to share and adapt your work, provided they give
            appropriate credit.`;

  if (submittedBy === 'author' && publishingModel.model === 'subscription') {
    description = `You have transferred or licenced (as the case may be) the copyright of this Submission to ${organizationName} on ${dateFormat(
      copyrightSubmittedDate,
      'mmmm d, yyyy'
    )}. You can review the details of this agreement below.`;
  }

  if (submittedBy === 'editorial') {
    description = `The Editorial Team has uploaded a signed Copyright and Publication Agreement for this Submission. No further action is required at this stage.`;
  }

  return description;
};

export const CopyrightFormSubmitted: React.FC<Props> = ({
  organizationName
}) => {
  const {
    copyrightForm,
    fieldsResponse,
    workflowResponse: {
      workflowData: { copyrightForm: copyrightFormWorkflow }
    }
  } = useSubmissionData();
  const publishingModelFFD = fieldsResponse.fields[
    'PublishingModel'
  ] as ISubmissionFieldResponseObject<FormFieldPublishingModel>;
  const currentPublishingModel = publishingModelFFD?.value;
  const type = copyrightForm?.type;

  const submittedBy: ISubmittedByType = type ? 'author' : 'editorial';

  const DownloadAgreementButton = () => {
    return (
      <ButtonText
        onClick={() => {
          copyrightForm?.file?.url && openFile(copyrightForm.file.url);
        }}
        leftIcon={<FontAwesomeIcon icon={faDownload} />}
        isDisabled={!copyrightForm?.file?.url}
      >
        Download Full Agreement
      </ButtonText>
    );
  };

  return (
    <>
      {copyrightForm && (
        <Box>
          <Flex flexDirection="column" gap={8}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingSection">
                Copyright and Publication Agreement
              </Text>
              {copyrightFormWorkflow?.submitted &&
                (copyrightForm.file?.url ? (
                  <Flex justifyContent="flex-start">
                    <DownloadAgreementButton />
                  </Flex>
                ) : (
                  <Flex justifyContent="flex-start">
                    <Tooltip
                      shouldWrapChildren
                      label={
                        'Agreement will be available to download in sometime.'
                      }
                      placement="bottom"
                    >
                      <DownloadAgreementButton />
                    </Tooltip>
                  </Flex>
                ))}
            </Flex>
            <Flex
              height="112px"
              width="100%"
              background="successSecondaryFill"
              justifyContent="center"
              alignItems="center"
              borderRadius="base"
              color="successPrimaryContentAccent"
              marginBottom={8}
            >
              <FontAwesomeIcon fontSize={40} icon={faCircleCheck} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">
                {copyrightSubmissionHeading(submittedBy)}
              </Text>
              <Text textStyle="textBody">
                {copyrightSubmissionDescription(
                  organizationName!,
                  currentPublishingModel!,
                  copyrightForm.date!,
                  submittedBy
                )}
              </Text>
            </Flex>
            {copyrightForm.type && <ReadonlyCopyrightForm />}
          </Flex>
        </Box>
      )}
    </>
  );
};
